import type {
  ExtractResource,
  Reference,
  ResourceType,
} from '@medplum/fhirtypes'
import { createReference } from '@seen/core/references'
import type { ComponentProps, JSX } from 'react'
import { ResourceAutocomplete } from '#src/components/input/ResourceAutocomplete.tsx'
import { FhirReference } from '#src/components/reference/FhirReference.tsx'

export interface FhirReferenceListProps<SearchType extends ResourceType> {
  value: Reference<ExtractResource<SearchType>>[] | undefined | null
  onChange?: (references: Reference<ExtractResource<SearchType>>[]) => void
  searchQuery: ComponentProps<
    typeof ResourceAutocomplete<SearchType>
  >['searchQuery']
  disabled?: boolean
}

export function FhirReferenceList<SearchType extends ResourceType>({
  value,
  onChange,
  searchQuery,
  disabled,
}: FhirReferenceListProps<SearchType>): JSX.Element {
  const references = value ?? []

  const handleReferenceChange = (
    index: number,
    newReference: Reference<ExtractResource<SearchType>> | undefined
  ): void => {
    if (newReference === undefined) {
      // Remove reference
      onChange?.(references.filter((_, i) => i !== index))
    } else {
      // Update reference
      const newReferences = [...references]
      newReferences[index] = newReference
      onChange?.(newReferences)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {references.map((reference, index) => (
        <FhirReference
          key={index}
          value={reference}
          onChange={(newRef) => handleReferenceChange(index, newRef)}
          disabled={disabled}
        />
      ))}
      {!disabled && (
        <ResourceAutocomplete<SearchType>
          key={references.length} // Reset this input once a new reference is added.
          name="reference"
          onOptionSubmit={(value) => {
            if (value) {
              onChange?.([...(references ?? []), createReference(value)])
            }
          }}
          placeholder="Add a reference"
          searchQuery={searchQuery}
        />
      )}
    </div>
  )
}
